import { resourcesCopy } from '@/copy/resources'
import { Field, Textarea } from '@/ui/Field/Field'
import { Inline } from '@/ui/layout/Inline'
import { Text } from '@/ui/layout/Text'
import { StatusBadge } from '@/ui/Status/Status'
import type { FieldProps, Fields, ResourceFormDescriptor } from './shared'

/**
 * Form-state key carrying the fields this form doesn't edit (status,
 * answer metadata) verbatim, so saving round-trips them instead of
 * clobbering them.
 */
const REST_KEY = '__rest'

interface QuestionRest {
  status?: string
  submitted_at?: number
  answered_at?: number
  answered_by?: string
  answer?: string
  annotations?: string | null
  [key: string]: unknown
}

function parseRest(raw: string | undefined): QuestionRest {
  try {
    return JSON.parse(raw || '{}') as QuestionRest
  } catch {
    return {}
  }
}

/**
 * Question editor: the question text and its options are editable; the
 * outcome (answer / status / timestamps) is shown read-only — answering
 * happens in the thread card so the asking agent is woken — and preserved
 * untouched on save.
 */
// eslint-disable-next-line react-refresh/only-export-components -- local component; the descriptor below is this file's only export
function QuestionFields({ fields, set }: FieldProps) {
  const rest = parseRest(fields[REST_KEY])
  const answered = rest.status === 'answered'
  return (
    <>
      <Inline gap="space-12">
        <StatusBadge tone={answered ? 'success' : 'warning'}>
          {rest.status ?? resourcesCopy.forms.question.defaultStatus}
        </StatusBadge>
        {rest.submitted_at ? (
          <Text variant="caption" tone="text-mute">
            {resourcesCopy.forms.question.asked(fmt(rest.submitted_at))}
          </Text>
        ) : null}
        {answered && rest.answer !== undefined && rest.answer !== null ? (
          <Text variant="caption" tone="text-mute">
            {resourcesCopy.forms.question.answer}{' '}
            <Text variant="caption" tone="text">
              {String(rest.answer)}
            </Text>
            {resourcesCopy.forms.question.answeredBy(
              rest.answered_by,
              rest.answered_at ? fmt(rest.answered_at) : undefined,
            )}
          </Text>
        ) : null}
      </Inline>
      <Field id="res-question" label={resourcesCopy.forms.question.question}>
        <Textarea
          id="res-question"
          value={fields.question ?? ''}
          onChange={(e) => set('question', e.target.value)}
          rows={4}
        />
      </Field>
      <Field
        id="res-options"
        label={resourcesCopy.forms.question.options}
        help={resourcesCopy.forms.question.optionsHelp}
      >
        <Textarea
          id="res-options"
          value={fields.options ?? ''}
          onChange={(e) => set('options', e.target.value)}
          rows={4}
          placeholder={resourcesCopy.forms.question.optionsPlaceholder}
          spellCheck={false}
        />
      </Field>
    </>
  )
}

function fmt(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

/** Options as editable lines: `label` or `label | value`. */
function optionsFromResource(
  options: { label: string; value: string }[] | undefined,
): string {
  return (options ?? [])
    .map((o) =>
      o.value && o.value !== o.label ? `${o.label} | ${o.value}` : o.label,
    )
    .join('\n')
}

function optionsToBody(raw: string): { label: string; value: string }[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, ...valueParts] = line.split('|')
      const value = valueParts.join('|').trim()
      return { label: label.trim(), value: value || label.trim() }
    })
}

export const questionForm: ResourceFormDescriptor = {
  Fields: QuestionFields,
  fromResource: (d: unknown) => {
    const { question, options, ...rest } = (d ?? {}) as {
      question?: string
      options?: { label: string; value: string }[]
    } & Record<string, unknown>
    return {
      question: question ?? '',
      options: optionsFromResource(options),
      [REST_KEY]: JSON.stringify(rest),
    }
  },
  toBody: (f: Fields) => ({
    ...parseRest(f[REST_KEY]),
    question: f.question ?? '',
    options: optionsToBody(f.options ?? ''),
  }),
}
